import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import OnboardingLogo from "../../assets/img/Logo.svg";
import rightArrow from "../../assets/img/right-arrow.png";
import { useMissingDocs } from "../../hooks/useMissingDocs";
import { AUTH_ROUTE, NON_AUTH_ROUTE } from "../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../store/application/slice";
import { logOutThunk } from "../../store/auth/thunks";
import CustomButton from "../UI/Buttons/CustomButton";
import LogOut from "../UI/Icons/LogOut";
import "./OnboardingSteps.scss";

const OnboardingSteps: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const isCompleteLater = useSelector(
    (state) => state.onboarding.isCompleteLater
  );
  const user = useSelector((state) => state.auth.user);
  const userpersonaldetails = useSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const stepCompletion = useSelector(
    (state) => state.onboarding.completedstatus
  );
  const personaldetails = useSelector(
    (state) => state.onboarding.personaldetails
  );
  const missingDocs = useMissingDocs();

  const isPersonalDetailsHaveResume = useMemo(() => {
    if(personaldetails?.resume === null || personaldetails === null) {
      return true;
    }
    return false;
  }, [personaldetails]);

  const handleLogOut = async () => {
    dispatch(showTransparentPreloaderAction());
    const result = await dispatch(logOutThunk());
    if (isFulfilled(result)) {
      navigate(NON_AUTH_ROUTE.LOGIN);
    }
    dispatch(hideTransparentPreloaderAction());
  };

  const stepsContainer = useMemo(() => {
    let val = "";

    if (location.pathname === "/onboarding/id-verfication") {
      return (
        <>
          <div className="progress-step" data-step="1">
            ID Verification
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="2">
            Personal Details
          </div>
        </>
      );
    } else if (location.pathname === "/onboarding/personal-details") {
      return (
        <>
          <div className="progress-step" data-step="1">
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="2">
            Personal Details
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="3"></div>
        </>
      );
    } else if (location.pathname === "/onboarding/qualifications") {
      return (
        <>
          <div className="progress-step" data-step="2">
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="3">
            Qualifications
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="4"></div>
        </>
      );
    } else if (location.pathname === "/onboarding/job-preferences") {
      return (
        <>
          <div className="progress-step" data-step="3">
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="4">
            Job Preferences
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="5"></div>
        </>
      );
    } else if (location.pathname === "/onboarding/online-interview") {
      return (
        <>
          <div className="progress-step" data-step="4">
            <img className="pl-15px" src={rightArrow} alt={"right Arrow"} />
          </div>
          <div className="progress-step" data-step="5">
            Online Interview
          </div>
        </>
      );
    }

    return val;
  }, [location]);

  return (
    <div className="sidebar p-50px d-flex flex-column justify-content-between gap-50 overflow-auto top-0 start-0">
      <div className="d-flex flex-column gap-50">
        <div className="d-flex align-items-center justify-content-between gap-20">
          <img
            src={OnboardingLogo}
            alt="Logo"
            width={150}
            className="img-fluid"
          />
          <div className="d-block d-lg-none">
            <CustomButton
              variant="primary"
              onClick={handleLogOut}
              className="btn-theme bg-transparent text-black p-0"
            >
              <LogOut />
            </CustomButton>
          </div>
        </div>
        <div className="d-flex flex-column gap-20">
          <div className="onboarding-stage d-flex flex-column gap-8">
            <div className="d-flex">
              <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                Stage 1
              </div>
            </div>
            <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
              <div className="fs-16 fw-700 text-black lh-1">
                Personal Details & Interview
              </div>
              <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
            </div>
            <div className="fs-16 fw-400 text-black d-md-none">
              Verify ID, upload qualifications, add your availabilities, record
              a video interview
            </div>
          </div>
          <div className="d-flex d-md-none">
            <div className="progress-container top-0 mobile-nav">
              {stepsContainer}
            </div>
          </div>
          <div className="d-none d-md-flex">
            <div className="progress-container top-0 desktop-nav">
              <Link
                to={AUTH_ROUTE.ID_VERIFICATION}
                className={`progress-step ${
                  stepCompletion?.id_verification === 1 ? "completed" : ""
                }`}
                data-step="1"
              >
                ID Verification
              </Link>
              {isCompleteLater === true ||
              userpersonaldetails?.phone_number !== null ||
              (user?.photo !== "" && user?.photo !== null) ||
              stepCompletion?.id_verification === 1 ? (
                <Link
                  to={AUTH_ROUTE.PERSONAL_DETAILS}
                  className={`progress-step ${
                    (stepCompletion?.personal_details === 1 && !isPersonalDetailsHaveResume) ? "completed" : ""
                  }`}
                  data-step="2"
                >
                  Personal Details
                </Link>
              ) : (
                <div data-step="2" className="progress-step">
                  Personal Details
                </div>
              )}
              {stepCompletion?.personal_details === 1 ||
              stepCompletion?.qualification === 1 ? (
                <Link
                  to={AUTH_ROUTE.QUALIFICATIONS}
                  className={`progress-step ${
                    (stepCompletion?.qualification === 1 && missingDocs.length === 0) ? "completed" : ""
                  }`}
                  data-step="3"
                >
                  Qualifications
                </Link>
              ) : (
                <div data-step="3" className="progress-step">
                  Qualifications
                </div>
              )}
              {stepCompletion?.qualification === 1 ||
              stepCompletion?.jobpreference === 1 ? (
                <Link
                  to={AUTH_ROUTE.JOB_PREFERENCES}
                  className={`progress-step ${
                    stepCompletion?.jobpreference === 1 ? "completed" : ""
                  }`}
                  data-step="4"
                >
                  Job Preferences
                </Link>
              ) : (
                <div data-step="4" className="progress-step">
                  Job Preferences
                </div>
              )}
              {stepCompletion?.online_interview === 1 ? (
                <Link
                  to={AUTH_ROUTE.ONLINE_INTERVIEW}
                  // className={`progress-step`}
                  className={`progress-step ${
                    stepCompletion?.online_interview === 1 ? "completed" : ""
                  }`}
                  data-step="5"
                >
                  Online Interview
                </Link>
              ) : (
                <div data-step="5" className="progress-step">
                  Online Interview
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="logout-button-container d-none d-lg-flex">
        <CustomButton
          variant="primary"
          onClick={handleLogOut}
          className="btn-theme bg-off-white text-black"
        >
          <LogOut />
          Log Out
        </CustomButton>
      </div>
    </div>
  );
};

export default OnboardingSteps;
